import { createFileRoute, useNavigate } from "@tanstack/react-router"

import { useCallback, useEffect } from "react"
import { supabase } from "@fieldbook/common/supabase"
import { type SubmitHandler, useForm } from "react-hook-form"
import { Spinner } from "@fieldbook/ui/spinner"
import { FormField } from "@fieldbook/ui/formField"
import { Button } from "@fieldbook/ui/button"

type FormData = {
  password: string
  password2: string
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
    watch,
  } = useForm<FormData>({ mode: "all" })

  const p1 = watch("password")
  const p2 = watch("password2")

  useEffect(() => {
    if (errors.root?.login) clearErrors("root.login")
  }, [clearErrors, errors.root?.login, p1, p2])

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      clearErrors()
      const {
        data: { user },
        error,
      } = await supabase.auth.updateUser({
        password: data.password,
      })

      if (error) {
        console.log("error", error)
        setError("root.login", { message: error.message })
        return
      }
      if (user) {
        navigate({ to: "/projects" })
      }
    },
    [clearErrors, setError, navigate],
  )

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="pwa:text-lg min-h-1/3 w-full max-w-md p-4 md:rounded-lg">
        <div>
          <h4 className="mb-2 text-xl font-bold">Set Password</h4>
          <p>Please set your new password.</p>
        </div>
        {errors.root?.login && (
          <div className="align-right flex rounded-md border bg-orange-200 p-2 text-xs text-orange-800">
            {errors.root.login.message}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
          <FormField
            label="Password"
            type="password"
            {...register("password", {
              required: "Required",
              minLength: { value: 6, message: "Minimum length of 6" },
            })}
            error={errors.password}
          />
          <FormField
            label="Confirm Password"
            type="password"
            {...register("password2", {
              required: "Required",
              minLength: { value: 6, message: "Minimum length of 6" },
              validate: (value) =>
                value === watch("password") || "Passwords do not match",
            })}
            error={errors.password2}
          />
          {errors.root?.login && (
            <div className="align-right flex rounded-md border bg-orange-200 p-2 text-xs text-orange-800">
              {errors.root.login.message}
            </div>
          )}
          {!errors.root?.login && <div className="h-[34px]" />}
          <Button disabled={!isValid || isSubmitting} type="submit">
            {!isSubmitting && "Submit"}
            {isSubmitting && <Spinner />}
          </Button>
        </form>
      </div>
    </div>
  )
}

export const Route = createFileRoute("/auth/reset-password")({
  component: ResetPasswordPage,
})
